import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, firstValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Article } from '../entities/article.model';
import { WordCard, Flashcard } from '../entities/wordcard.model';
import { AuthService } from './auth.service';
import { User } from '../entities/user.model';

@Injectable({
  providedIn: 'root'
})
export class TextToSpeechService {
  private backendUrl: string;
  private ttsEndpoint: string;
  private currentAudio: HTMLAudioElement | null = null;
  private isPlaying = false;
  private currentUser: User | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {
    // Set the backend URL based on the environment
    this.backendUrl = environment.production
      ? 'https://us-central1-gno-knews.cloudfunctions.net'
      : 'http://localhost:8080';
    
    // Set the TTS endpoint
    this.ttsEndpoint = `${this.backendUrl}/text_to_speech`;

    // Subscribe to user changes
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  /**
   * Main function to vocalize an article at the selected level
   * @param article - The article to vocalize
   * @param selectedLevel - The level to read (1, 2, or 3)
   * @returns Promise that resolves when audio starts playing
   */
  async vocalizeArticle(article: Article, selectedLevel: '1' | '2' | '3'): Promise<void> {
    try {
      // Stop any currently playing audio
      this.stopCurrentAudio();

      // Get the text content for the selected level
      const textContent = this.getTextForLevel(article, selectedLevel);
      
      if (!textContent) {
        throw new Error(`No text content found for level ${selectedLevel}`);
      }

      // Get the audio from the backend
      const audioBase64 = await firstValueFrom(this.getAudioFromBackend(article, selectedLevel));
      
      if (!audioBase64) {
        throw new Error('Failed to get audio from server');
      }
      
      // Convert base64 to blob and play the audio
      const audioBlob = this.base64ToBlob(audioBase64);
      await this.playAudio(audioBlob);

    } catch (error) {
      console.error('Error vocalizing article:', error);
      throw error;
    }
  }

  /**
   * Main function to vocalize a word card or flashcard
   * @param wordCard - The word card or flashcard to vocalize
   * @param targetLanguage - The target language code (e.g., 'en', 'es', 'fr')
   * @returns Promise that resolves when audio starts playing
   */
  async vocalizeWordCard(wordCard: WordCard | Flashcard, targetLanguage: string): Promise<void> {
    try {
      // Stop any currently playing audio
      this.stopCurrentAudio();

      // Get the text content from the word card
      const textContent = this.getTextForWordCard(wordCard);
      
      if (!textContent) {
        throw new Error('No text content found in word card');
      }

      // Get the audio from the backend
      const audioBase64 = await firstValueFrom(this.getAudioFromBackendForWordCard(wordCard, targetLanguage));
      
      if (!audioBase64) {
        throw new Error('Failed to get audio from server');
      }
      
      // Convert base64 to blob and play the audio
      const audioBlob = this.base64ToBlob(audioBase64);
      await this.playAudio(audioBlob);

    } catch (error) {
      console.error('Error vocalizing word card:', error);
      throw error;
    }
  }

  /**
   * Call the backend endpoint to get TTS audio
   */
  private getAudioFromBackend(article: Article, level: '1' | '2' | '3'): Observable<string> {
    // Get the text content for the selected level
    const textContent = this.getTextForLevel(article, level);
    
    // Get voice configuration for the language
    const voiceConfig = this.getVoiceForLanguage(article.language);
    
    const data = {
      text: textContent,
      language: voiceConfig.languageCode,
      voice: voiceConfig.voiceName,
      gender: voiceConfig.gender,
      speed: voiceConfig.speed || 2
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(this.ttsEndpoint, data, { headers }).pipe(
      map((response: any) => response.audio), // Extract the base64 audio
      catchError((error) => {
        console.error('Error fetching TTS audio:', error);
        throw new Error('Failed to fetch audio from server');
      })
    );
  }

  /**
   * Call the backend endpoint to get TTS audio for word cards
   */
  private getAudioFromBackendForWordCard(wordCard: WordCard | Flashcard, targetLanguage: string): Observable<string> {
    // Get the text content for the word card
    const textContent = this.getTextForWordCard(wordCard);
    
    // Get voice configuration for the language
    const voiceConfig = this.getVoiceForLanguage(targetLanguage);
    
    const data = {
      text: textContent,
      language: voiceConfig.languageCode,
      voice: voiceConfig.voiceName,
      gender: voiceConfig.gender,
      speed: voiceConfig.speed || 2
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(this.ttsEndpoint, data, { headers }).pipe(
      map((response: any) => response.audio), // Extract the base64 audio
      catchError((error) => {
        console.error('Error fetching TTS audio for word card:', error);
        throw new Error('Failed to fetch audio from server');
      })
    );
  }

  /**
   * Extract text content for the specified level from the article
   */
  private getTextForLevel(article: Article, level: '1' | '2' | '3'): string {
    const articleLanguage = article.language;
    
    if (!article.articleText[articleLanguage] || !article.articleText[articleLanguage][level]) {
      throw new Error(`No text found for language ${articleLanguage} at level ${level}`);
    }

    // Combine title and article text for a complete reading experience
    const title = article.title[articleLanguage]?.[level] || '';
    const content = article.articleText[articleLanguage][level];
    
    return title ? `${title}. ${content}` : content;
  }

  /**
   * Extract text content from a word card for TTS
   */
  private getTextForWordCard(wordCard: WordCard | Flashcard): string {
    // Combine the root word, and first examples for pronunciation
    const rootWord = wordCard.root_word || '';
    const example1 = wordCard.examples?.[0] || '';
    return `${rootWord}. ${example1}`;
  }

  /**
   * Play the synthesized audio
   */
  private async playAudio(audioBlob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Create audio element
        const audioUrl = URL.createObjectURL(audioBlob);
        this.currentAudio = new Audio(audioUrl);
        
        // Set up event listeners
        this.currentAudio.onloadeddata = () => {
          this.isPlaying = true;
          this.currentAudio!.play();
          resolve();
        };
        
        this.currentAudio.onended = () => {
          this.isPlaying = false;
          this.cleanupAudio();
        };
        
        this.currentAudio.onerror = (error) => {
          this.isPlaying = false;
          this.cleanupAudio();
          reject(new Error('Audio playback failed'));
        };
        
        // Load the audio
        this.currentAudio.load();
        
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Stop currently playing audio
   */
  public stopCurrentAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.isPlaying = false;
      this.cleanupAudio();
    }
  }

  /**
   * Check if audio is currently playing
   */
  public isCurrentlyPlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Clean up audio resources
   */
  private cleanupAudio(): void {
    if (this.currentAudio) {
      if (this.currentAudio.src) {
        URL.revokeObjectURL(this.currentAudio.src);
      }
      this.currentAudio = null;
    }
  }

  /**
   * Wake up the TTS endpoint (similar to translate service)
   */
  wakeUpEndpoint(): void {
    this.http.post(this.ttsEndpoint, { ping: true }).subscribe({
      next: (response) => {
        console.log('TTS endpoint responded with 200', response);
      },
      error: (error) => {
        console.log('TTS endpoint did not respond with 200:', error.status);
      }
    });
  }

  /**
   * Get voice configuration for the given language based on user preferences
   * 
   * This method selects appropriate Chirp 3 HD voices based on:
   * 1. User's gender preference (male/female)
   * 2. Language availability for that gender
   * 3. Graceful fallback to available voices if preferred gender isn't available
   * 
   * Voice names are based on Google Cloud TTS Chirp 3 HD voice catalog.
   * If a language doesn't have the requested gender voice, it falls back to
   * the opposite gender. If the language isn't supported, it defaults to English.
   */
  private getVoiceForLanguage(languageCode: string): { languageCode: string; voiceName: string; gender: string; speed?: number } {
    // Get user preferences
    const userFemaleVoice = this.currentUser?.femaleVoice !== undefined ? this.currentUser.femaleVoice : true;
    const userAudioSpeed = this.currentUser?.audioSpeed || 2;
    const requestedGender = userFemaleVoice ? 'FEMALE' : 'MALE';
    
    // Extract the base language code (e.g., 'en' from 'en-US')
    const baseLanguage = languageCode.split('-')[0];
    
    // Map language codes to their TTS configuration with gender-specific voices
    const languageVoiceMap: { [key: string]: { 
      languageCode: string; 
      femaleVoices: string[]; 
      maleVoices: string[] 
    } } = {
      'en': {
        languageCode: 'en-US',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'es': {
        languageCode: 'es-ES',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'fr': {
        languageCode: 'fr-FR',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'de': {
        languageCode: 'de-DE',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'it': {
        languageCode: 'it-IT',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'pt': {
        languageCode: 'pt-BR',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'ja': {
        languageCode: 'ja-JP',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'ko': {
        languageCode: 'ko-KR',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'zh': {
        languageCode: 'cmn-CN',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'ar': {
        languageCode: 'ar-XA',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'hi': {
        languageCode: 'hi-IN',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      // Special cases for languages with limited voice availability
      'ru': {
        languageCode: 'ru-RU',
        femaleVoices: ['Aoede', 'Kore', 'Leda', 'Zephyr'], // Only these female voices are available for Russian
        maleVoices: ['Charon', 'Fenrir', 'Orus', 'Puck'] // Only these male voices are available for Russian
      },
      'da': {
        languageCode: 'da-DK',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'nl': {
        languageCode: 'nl-NL',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'sv': {
        languageCode: 'sv-SE',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'nb': {
        languageCode: 'nb-NO',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'no': {
        languageCode: 'nb-NO',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'pl': {
        languageCode: 'pl-PL',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'tr': {
        languageCode: 'tr-TR',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'fi': {
        languageCode: 'fi-FI',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'uk': {
        languageCode: 'uk-UA',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'vi': {
        languageCode: 'vi-VN',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'th': {
        languageCode: 'th-TH',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      },
      'id': {
        languageCode: 'id-ID',
        femaleVoices: ['Aoede', 'Achernar', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
        maleVoices: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi']
      }
    };

    // Get language configuration or default to English
    const languageConfig = languageVoiceMap[baseLanguage] || languageVoiceMap['en'];
    
    // Select appropriate voice based on requested gender
    let selectedVoice: string;
    let actualGender: string;
    
    if (requestedGender === 'FEMALE' && languageConfig.femaleVoices.length > 0) {
      // Use the first available female voice
      selectedVoice = languageConfig.femaleVoices[0];
      actualGender = 'FEMALE';
    } else if (requestedGender === 'MALE' && languageConfig.maleVoices.length > 0) {
      // Use the first available male voice
      selectedVoice = languageConfig.maleVoices[0];
      actualGender = 'MALE';
    } else if (languageConfig.femaleVoices.length > 0) {
      // Fallback to female voice if requested gender not available
      selectedVoice = languageConfig.femaleVoices[0];
      actualGender = 'FEMALE';
      console.warn(`Requested ${requestedGender} voice not available for ${baseLanguage}, falling back to FEMALE voice: ${selectedVoice}`);
    } else if (languageConfig.maleVoices.length > 0) {
      // Fallback to male voice if no female voices available
      selectedVoice = languageConfig.maleVoices[0];
      actualGender = 'MALE';
      console.warn(`Requested ${requestedGender} voice not available for ${baseLanguage}, falling back to MALE voice: ${selectedVoice}`);
    } else {
      // Ultimate fallback to English Aoede female
      selectedVoice = 'Aoede';
      actualGender = 'FEMALE';
      console.warn(`No voices available for ${baseLanguage}, falling back to English FEMALE voice: ${selectedVoice}`);
    }
    
    console.log(`TTS Voice Selection - Language: ${baseLanguage}, Requested: ${requestedGender}, Selected: ${selectedVoice} (${actualGender})`);
    
    return {
      languageCode: languageConfig.languageCode,
      voiceName: selectedVoice,
      gender: actualGender,
      speed: userAudioSpeed
    };
  }

  /**
   * Convert base64 string to audio blob
   */
  private base64ToBlob(base64: string): Blob {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'audio/mp3' });
  }
}
